// EF-4 (MVP 5.5) — traces some browser extensions leave in the page.
//
// THE LIMIT, stated plainly: a web page CANNOT see, list or turn off a
// candidate's extensions. Extension enumeration is an extension-only
// privilege in Chrome/Edge, Firefox randomises extension ids per install,
// Safari exposes nothing, and a private window cannot be verified either. So
// "candidates must turn off extensions" is an honour step at the gate
// (instructions + an acknowledgement, `extensions_confirmed` on /start) and
// THIS module is the honest telemetry beside it: a bounded table of DOM
// markers that well-known extensions inject (Grammarly, password managers,
// translators, "AI helper" overlays), plus a count of top-level DOM nodes
// that are not ours.
//
// It detects a FRACTION of extensions and can never prove absence — and
// honest candidates run Grammarly and 1Password too. So the result rides
// `attempt_started` as CONTEXT for the recruiter's attempt page only:
// backend/risk.py never reads it and nothing here blocks or flags. Copy on the
// recruiter side says "signs of an extension were seen", never "cheating".
//
// Pure over a Document, no side effects, so it is unit-testable in jsdom and
// safe to run at the gate right before `attempt_started` is emitted.

export interface ExtensionProbeResult {
  /** Short slugs from the marker table below, deduplicated, in table order.
   *  Bounded (EXTENSION_TRACES_MAX × EXTENSION_TRACE_MAX_LENGTH) to match the
   *  server's ingest limits on `AttemptStartedPayload.extension_traces`. */
  traces: string[];
  /** Direct children of <html> (other than <head>/<body>) and of <body> that
   *  are not ours. Extensions that mount their UI at the top level land here
   *  even when the marker table does not know them. */
  foreignNodes: number;
}

// Mirrors `AttemptStartedPayload` in backend/models/snapshots.py.
export const EXTENSION_TRACES_MAX = 20;
export const EXTENSION_TRACE_MAX_LENGTH = 80;
const FOREIGN_NODES_MAX = 10_000;

interface Marker {
  slug: string;
  /** Any one selector matching counts as a hit. Tag-name selectors are custom
   *  elements the extension defines; attribute/id selectors are what its
   *  content script stamps on the page. */
  selectors: readonly string[];
}

// Best-effort and deliberately SHORT: every entry is a marker seen in the
// wild for a widely installed extension, and a miss costs nothing (the
// foreign-node count still catches a top-level mount). Adding a row needs no
// server change — the server stores the slug as an opaque bounded string.
const MARKERS: readonly Marker[] = [
  {
    slug: "grammarly",
    selectors: [
      "grammarly-desktop-integration",
      "grammarly-extension",
      "grammarly-popups",
      "[data-gr-ext-installed]",
      "[data-new-gr-c-s-check-loaded]",
    ],
  },
  {
    slug: "lastpass",
    selectors: ["[data-lastpass-icon-root]", "[data-lastpass-root]", "#lastpass-root"],
  },
  {
    slug: "1password",
    selectors: [
      "com-1password-button",
      "com-1password-notification",
      "com-1password-menu",
      "[data-onepassword-extension]",
    ],
  },
  {
    slug: "dashlane",
    selectors: ["[data-dashlanecreated]", "[data-dashlane-rid]", "[id^='dashlane-']"],
  },
  {
    slug: "bitwarden",
    selectors: ["#bit-notification-bar", "#bit-notification-bar-iframe", "[id^='bit-notification']"],
  },
  { slug: "honey", selectors: ["#honeyContainer", "[id^='honey-']"] },
  {
    slug: "dark_reader",
    selectors: ["style.darkreader", "[data-darkreader-mode]", "meta[name='darkreader']"],
  },
  {
    // Chrome's built-in page translation, not an extension — but it rewrites
    // the DOM the same way (and it crashes React: memory
    // project_google_translate_react_removechild), so it is worth a slug.
    slug: "google_translate",
    selectors: [
      "#goog-gt-tt",
      "html.translated-ltr",
      "html.translated-rtl",
      ".goog-te-banner-frame",
      ".skiptranslate",
    ],
  },
  { slug: "loom", selectors: ["#loom-companion-mv3", "[id^='loom-']"] },
  {
    // The "AI helper" sidebars — the extensions this step is really about.
    slug: "ai_sidebar",
    selectors: [
      "[id^='monica-']",
      "monica-app",
      "#sider-chat",
      "[id^='sider-']",
      "harpa-ai",
      "[id^='harpa-']",
      "#MAXAI_SIDEBAR_WRAPPER",
      "[id^='maxai-']",
      "[id^='merlin-']",
      "[id^='chatgpt-']",
    ],
  },
];

// What WE put at the top level of the document: the app root, Vite's dev
// overlay, the sonner toaster portal, Monaco's floating widgets. Everything
// else under <html>/<body> is foreign — a content script's mount point, an
// injected iframe, a floating toolbar.
const OUR_TAGS: ReadonlySet<string> = new Set([
  "SCRIPT",
  "STYLE",
  "LINK",
  "NOSCRIPT",
  "TEMPLATE",
  "META",
  "TITLE",
  "BASE",
]);
const OUR_SELECTORS: readonly string[] = [
  "#root",
  "[data-sonner-toaster]",
  ".monaco-aria-container",
  ".context-view",
  ".monaco-editor",
  ".monaco-hover",
  ".monaco-menu-container",
  ".monaco-component",
  "vite-error-overlay",
  "[data-vite-dev-id]",
];

function matchesAny(el: Element, selectors: readonly string[]): boolean {
  for (const sel of selectors) {
    try {
      if (el.matches(sel)) return true;
    } catch {
      // A selector this engine cannot parse is a miss, never a crash.
    }
  }
  return false;
}

function isOurs(el: Element): boolean {
  if (OUR_TAGS.has(el.tagName)) return true;
  if (el.tagName.toLowerCase().startsWith("monaco-")) return true;
  return matchesAny(el, OUR_SELECTORS);
}

function anyPresent(doc: Document, selectors: readonly string[]): boolean {
  for (const sel of selectors) {
    try {
      if (doc.querySelector(sel) !== null) return true;
    } catch {
      // Unparseable selector — a miss.
    }
  }
  return false;
}

/**
 * Probe `doc` for extension traces. Never throws: a document with no <html>
 * or <body> (a detached test document, a mid-teardown page) reads as clean.
 */
export function probeExtensionTraces(doc: Document = document): ExtensionProbeResult {
  const traces: string[] = [];
  for (const marker of MARKERS) {
    if (traces.length >= EXTENSION_TRACES_MAX) break;
    if (anyPresent(doc, marker.selectors)) {
      traces.push(marker.slug.slice(0, EXTENSION_TRACE_MAX_LENGTH));
    }
  }

  let foreignNodes = 0;
  const html = doc.documentElement;
  if (html) {
    for (const el of Array.from(html.children)) {
      if (el.tagName === "HEAD" || el.tagName === "BODY") continue;
      if (!isOurs(el)) foreignNodes += 1;
    }
  }
  if (doc.body) {
    for (const el of Array.from(doc.body.children)) {
      if (!isOurs(el)) foreignNodes += 1;
    }
  }
  return { traces, foreignNodes: Math.min(foreignNodes, FOREIGN_NODES_MAX) };
}
